import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { ENV } from "@app/constants/env";
import { getToken } from "@app/helpers/authProvider";
import { customs } from "@feature/types/customs.type";

type ApiResponse<T> = {
  data: T;
  success: boolean;
  message?: string;
};

const API_BASE_URL = ENV.BASE_API_URL +  ENV.JOB_APIURL;

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
});

const authBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (incomingArgs, api, extraOptions) => {
  let args: string | FetchArgs = incomingArgs;

  if (typeof args === "string") {
    args = { url: args };
  }

  try {
    const token = await getToken();
    if (token) {
      const headers =
        (args as FetchArgs).headers instanceof Headers
          ? (args as FetchArgs).headers
          : new Headers((args as FetchArgs).headers);

      headers.set("Authorization", `Bearer ${token}`);
      (args as FetchArgs).headers = headers;
    }

    const result = await rawBaseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401 && token) {
      // refresh token flow could go here
    }

    return result;
  } catch {
    return {
      error: {
        status: 500,
        data: "Token retrieval failed",
      },
    } as FetchBaseQueryError;
  }
};

// Normalizes API responses that may be wrapped with a { data, success } envelope
function normalizeApiResponse<T>(response: any): ApiResponse<T> {
  if (response && typeof response === "object" && "data" in response) {
    return response as ApiResponse<T>;
  }
  return { data: response as T, success: true };
}

export const customsApiSlice = createApi({
  // Use a unique reducerPath to avoid colliding with the other `customsApi`
  reducerPath: "efairsJobCustomsApi",
  baseQuery: authBaseQuery,
  tagTypes: ["Customs"],
  endpoints: (builder) => ({
    getCustomsData: builder.query<customs[], { workOrderId: number }>({
      query: ({ workOrderId }) => `Customs/GetCustomsByWorkOrder/${workOrderId}`,
      providesTags: ["Customs"],
    }),
    getCustomsById: builder.query<customs, { id: number }>({
      query: ({ id }) => `Customs/GetCustomsById?Customs_Id=${id}`,
      providesTags: ["Customs"],
    }),
    createCustomsData: builder.mutation<
      ApiResponse<customs>,
      customs
    >({
      query: (customsBody) => ({
        url: "Customs/CreateCustoms",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: customsBody,
      }),
      invalidatesTags: ["Customs"],
      transformResponse: (response: any) => normalizeApiResponse<customs>(response),
    }),
    updateCustomsData: builder.mutation<
      ApiResponse<customs>,
      customs
    >({
      query: (customsBody) => ({
        url: "Customs/UpdateCustoms",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: customsBody,
      }),
      invalidatesTags: ["Customs"],
      transformResponse: (response: any) => normalizeApiResponse<customs>(response),
    }),
    deleteCustomsData: builder.mutation<
      ApiResponse<void>,
      { customId: number; userId: number }
    >({
      query: ({ customId, userId }) => ({
        url: `Customs/DeleteCustoms/${customId}/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Customs"],
    }),
    getCustomsDetailsById: builder.query<customs, { customsAuditId: number }>({
      query: ({ customsAuditId }) => `Customs/GetCustomsDetailsById/${customsAuditId}`,
      providesTags: ["Customs"],
    }),
    getCustomsDetails: builder.query<customs[], { customId: number }>({
      query: ({ customId }) => `Customs/GetCustomsDetails/${customId}`,
      providesTags: ["Customs"],
    }),
    createCustomsDetails: builder.mutation<
      ApiResponse<customs>,
      customs
    >({
      query: (customsDetailsBody) => ({
        url: "Customs/CreateCustomsDetails",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: customsDetailsBody,
      }),
      invalidatesTags: ["Customs"],
      transformResponse: (response: any) => normalizeApiResponse<customs>(response),
    }),
    updateCustomsDetails: builder.mutation<
      ApiResponse<customs>,
      customs
    >({
      query: (customsDetailsBody) => ({
        url: "Customs/UpdateCustomsDetails",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: customsDetailsBody,
      }),
      invalidatesTags: ["Customs"],
      transformResponse: (response: any) => normalizeApiResponse<customs>(response),
    }),
    deleteCustomsDetail: builder.mutation<
      ApiResponse<boolean>,
      { customsAuditId: number; userId: number }
    >({
      query: ({ customsAuditId, userId }) => ({
        url: `Customs/DeleteCustomsDetails/${customsAuditId}/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Customs"],
    }),
    getCustomsDetailsForAudit: builder.query({
      query: ({ customId }) => `Customs/GetCustomsDetails/${customId}`,
      providesTags: ["Customs"],
    }),
  }),
});

export const {
  useGetCustomsDataQuery,
  useGetCustomsByIdQuery,
  useCreateCustomsDataMutation,
  useUpdateCustomsDataMutation,
  useDeleteCustomsDataMutation,
  useGetCustomsDetailsByIdQuery,
  useGetCustomsDetailsQuery,
  useCreateCustomsDetailsMutation,
  useUpdateCustomsDetailsMutation,
  useDeleteCustomsDetailMutation,
  useLazyGetCustomsDetailsForAuditQuery,
} = customsApiSlice; 